import ScheduleEntry, { WeekConflictInfo } from '../models/ScheduleEntry';
import Teacher from '../models/Teacher';
import Group from '../models/Group';
import Subject from '../models/Subject';
import { availabilityService } from './AvailabilityService';
import {
  makeConflictId as makeDismissedConflictId,
  isDismissed as isConflictDismissed,
  toggleDismissed as toggleConflictDismissed,
  cleanupDismissed,
  filterActiveReasons as filterActiveDismissedReasons,
  onDismissedChange,
} from './dismissedConflicts';

export type ConflictStatus = 'green' | 'orange' | 'red';

interface ConflictCalculationOptions {
  batchSize?: number;
  onProgress?: (processed: number, total: number) => void;
  peerEntries?: ScheduleEntry[];
}

interface ConflictCacheEntry {
  conflictsByWeek: Map<string, WeekConflictInfo>;
  timestamp: Date;
  contextFingerprint: string;
}

/**
 * ConflictManager handles conflict calculations for schedule entries
 * in an efficient, batch-oriented manner with per-week conflict tracking.
 */
class ConflictManager {
  private static instance: ConflictManager | null = null;

  // Cache for conflict calculations — keys include entry + schedule/asset context fingerprints
  private conflictCache = new Map<string, ConflictCacheEntry>();

  // Track which entries depend on which other entries for conflict calculation
  private dependencyGraph = new Map<string, Set<string>>();

  // Default cache TTL (time to live) in milliseconds
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): ConflictManager {
    if (!ConflictManager.instance) {
      ConflictManager.instance = new ConflictManager();
    }
    return ConflictManager.instance;
  }

  /**
   * Calculate conflicts for all entries in a batch
   */
  async calculateConflictsForEntries(
    entries: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    options: ConflictCalculationOptions = {}
  ): Promise<ScheduleEntry[]> {
    const {
      batchSize = 50,
      onProgress,
      peerEntries
    } = options;

    const resolvedPeerEntries = peerEntries ?? entries;

    console.log(`[ConflictManager] Starting batch conflict calculation for ${entries.length} entries`);

    // Build dependency graph first
    this.buildDependencyGraph(resolvedPeerEntries);

    const contextFingerprint = this.buildContextFingerprint(resolvedPeerEntries, teachers, groups, subjects);

    const updatedEntries: ScheduleEntry[] = [];
    let processedCount = 0;

    // Process entries in batches to avoid blocking the UI
    for (let i = 0; i < entries.length; i += batchSize) {
      const batch = entries.slice(i, i + batchSize);

      // Process each entry in the batch
      for (const entry of batch) {
        const updatedEntry = this.calculateConflictForEntry(
          entry,
          resolvedPeerEntries,
          teachers,
          groups,
          subjects,
          contextFingerprint
        );
        updatedEntries.push(updatedEntry);
        processedCount++;

        // Report progress if callback provided
        if (onProgress) {
          onProgress(processedCount, entries.length);
        }
      }

      // Yield control to prevent blocking
      if (i + batchSize < entries.length) {
        await new Promise(resolve => setTimeout(resolve, 1));
      }
    }

    console.log(`[ConflictManager] Completed batch conflict calculation`);
    return updatedEntries;
  }

  /**
   * Calculate conflicts for a single entry across all its active weeks
   */
  calculateConflictForEntry(
    entry: ScheduleEntry,
    allEntries: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    contextFingerprint?: string
  ): ScheduleEntry {
    // Skip conflict checking for events or entries without proper time/room data
    if (entry.type === 'event' || !entry.isScheduled() || !entry.isTimeValid) {
      const clonedEntry = entry.clone();
      clonedEntry.clearAllConflictStatuses();
      return clonedEntry;
    }

    const resolvedContextFingerprint = contextFingerprint
      ?? this.buildContextFingerprint(allEntries, teachers, groups, subjects);
    const cacheKey = this.generateCacheKey(entry, resolvedContextFingerprint);
    const cached = this.conflictCache.get(cacheKey);

    if (
      cached
      && this.isCacheValid(cached.timestamp)
      && cached.contextFingerprint === resolvedContextFingerprint
    ) {
      const clonedEntry = entry.clone();
      cached.conflictsByWeek.forEach((conflictInfo) => {
        clonedEntry.setConflictStatusForWeek(
          conflictInfo.week,
          conflictInfo.year,
          conflictInfo.status,
          conflictInfo.reasons
        );
      });
      return clonedEntry;
    }

    // Calculate conflicts using AvailabilityService
    try {
      const startMinutes = entry.startMinutes;
      if (startMinutes === null) {
        const clonedEntry = entry.clone();
        clonedEntry.clearAllConflictStatuses();
        return clonedEntry;
      }

      const availabilityResult = availabilityService.checkAvailability(
        entry,
        entry.room,
        entry.day,
        startMinutes,
        allEntries,
        teachers,
        groups,
        subjects
      );

      this.conflictCache.set(cacheKey, {
        conflictsByWeek: availabilityResult.conflictsByWeek,
        timestamp: new Date(),
        contextFingerprint: resolvedContextFingerprint
      });

      const clonedEntry = entry.clone();
      clonedEntry.clearAllConflictStatuses();

      availabilityResult.conflictsByWeek.forEach((weekConflict) => {
        clonedEntry.setConflictStatusForWeek(
          weekConflict.week,
          weekConflict.year,
          weekConflict.status,
          weekConflict.reasons
        );
      });

      return clonedEntry;

    } catch (error) {
      console.error(`[ConflictManager] Error calculating conflicts for entry ${entry.id}:`, error);
      const clonedEntry = entry.clone();
      clonedEntry.clearAllConflictStatuses();
      return clonedEntry;
    }
  }

  /**
   * Recalculate conflicts for specific entries and their dependencies
   */
  async recalculateConflictsForChangedEntries(
    changedEntryIds: string[],
    allEntries: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    options: ConflictCalculationOptions = {}
  ): Promise<ScheduleEntry[]> {
    console.log(`[ConflictManager] Recalculating conflicts for changed entries:`, changedEntryIds);

    const resolvedPeerEntries = options.peerEntries ?? allEntries;

    // Rebuild graph so teacher/group/room edits get correct neighbors
    this.buildDependencyGraph(resolvedPeerEntries);

    const entriesToRecalculate = new Set<string>(changedEntryIds);

    for (const changedId of changedEntryIds) {
      const dependencies = this.dependencyGraph.get(changedId);
      if (dependencies) {
        dependencies.forEach(depId => entriesToRecalculate.add(depId));
      }
    }

    // Context fingerprint changes with the schedule, so old keys miss naturally;
    // still prune keys for touched entries to keep the cache bounded.
    entriesToRecalculate.forEach(entryId => {
      this.clearCacheForEntry(entryId);
    });

    const entryMap = new Map(allEntries.map(entry => [entry.id, entry]));
    const entriesToUpdate = Array.from(entriesToRecalculate)
      .map(id => entryMap.get(id))
      .filter((entry): entry is ScheduleEntry => entry !== undefined);

    console.log(`[ConflictManager] Updating ${entriesToUpdate.length} entries due to dependencies`);

    const contextFingerprint = this.buildContextFingerprint(resolvedPeerEntries, teachers, groups, subjects);
    const updatedEntries: ScheduleEntry[] = [];
    const unchangedEntries = allEntries.filter(entry => !entriesToRecalculate.has(entry.id));

    for (const entry of entriesToUpdate) {
      const updatedEntry = this.calculateConflictForEntry(
        entry,
        resolvedPeerEntries,
        teachers,
        groups,
        subjects,
        contextFingerprint
      );
      updatedEntries.push(updatedEntry);
    }

    return [...updatedEntries, ...unchangedEntries];
  }

  /**
   * Build dependency graph to track which entries affect each other
   * Entries that share teachers, groups, rooms, or time slots are dependent
   */
  private buildDependencyGraph(entries: ScheduleEntry[]): void {
    this.dependencyGraph.clear();

    entries.forEach(entry => {
      this.dependencyGraph.set(entry.id, new Set());
    });

    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const entry1 = entries[i];
        const entry2 = entries[j];

        if (entry1 && entry2 && this.areEntriesDependent(entry1, entry2)) {
          this.dependencyGraph.get(entry1.id)?.add(entry2.id);
          this.dependencyGraph.get(entry2.id)?.add(entry1.id);
        }
      }
    }

    console.log(`[ConflictManager] Built dependency graph with ${this.dependencyGraph.size} nodes`);
  }

  /**
   * Check if two entries are dependent (changes to one could affect the other's conflicts)
   */
  private areEntriesDependent(entry1: ScheduleEntry, entry2: ScheduleEntry): boolean {
    if (entry1.room && entry2.room && entry1.room === entry2.room && entry1.day === entry2.day) {
      return true;
    }

    if (this.entriesShareTeacher(entry1, entry2)) {
      return true;
    }

    if (entry1.groups && entry2.groups && entry1.groups.length > 0 && entry2.groups.length > 0) {
      const groups1 = new Set(entry1.groups.map(g => typeof g === 'object' ? String(g.id ?? g.name ?? g.group_code) : String(g)));
      const groups2 = new Set(entry2.groups.map(g => typeof g === 'object' ? String(g.id ?? g.name ?? g.group_code) : String(g)));

      for (const group of Array.from(groups1)) {
        if (groups2.has(group)) {
          return true;
        }
      }
    }

    return false;
  }

  private entriesShareTeacher(entry1: ScheduleEntry, entry2: ScheduleEntry): boolean {
    const teachers1 = this.getTeacherNames(entry1);
    const teachers2 = this.getTeacherNames(entry2);
    if (teachers1.length === 0 || teachers2.length === 0) return false;
    const set2 = new Set(teachers2.map(t => t.toLowerCase()));
    return teachers1.some(t => set2.has(t.toLowerCase()));
  }

  private getTeacherNames(entry: ScheduleEntry): string[] {
    const names = new Set<string>();
    if (entry.teacher) names.add(entry.teacher);
    if (Array.isArray(entry.teachers) && entry.teachers.length > 0) {
      entry.teachers.forEach(t => {
        if (t?.name) names.add(t.name);
      });
    }
    return Array.from(names);
  }

  /**
   * Fingerprint of fields that define this entry as a conflict subject / peer.
   * Stable ordering so the same schedule always hashes the same way.
   */
  fingerprintEntry(entry: ScheduleEntry): string {
    const groups = (entry.groups || [])
      .map(g => (typeof g === 'object' ? String(g.id ?? g.name ?? g.group_code ?? '') : String(g)))
      .filter(Boolean)
      .sort()
      .join(',');

    const teachers = this.getTeacherNames(entry).sort().join(',');

    return [
      entry.id,
      entry.type || 'class',
      entry.room || '',
      entry.day || '',
      entry.timeStart || '',
      entry.timeEnd || '',
      teachers,
      groups,
      entry.weekSeries || '',
      entry.course || entry.subject || '',
      entry.periodId ?? ''
    ].join('|');
  }

  /**
   * Fingerprint of assets that feed availability rules (prefs, room constraints).
   */
  fingerprintAssets(
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[]
  ): string {
    const teacherPart = (teachers || [])
      .map(t => {
        const prefs = (t.preferences || [])
          .map(p => `${p.day}:${p.startTime}-${p.endTime}:${p.status}`)
          .sort()
          .join(';');
        return `${t.id || t.name}|${prefs}`;
      })
      .sort()
      .join('~');

    const groupPart = (groups || [])
      .map(g => {
        const prefs = (g.preferences || [])
          .map((p: any) => {
            const day = p.day ?? '';
            const start = p.startTime ?? p.start_time ?? '';
            const end = p.endTime ?? p.end_time ?? '';
            const status = p.status ?? '';
            return `${day}:${start}-${end}:${status}`;
          })
          .sort()
          .join(';');
        return `${g.id || g.name}|${prefs}`;
      })
      .sort()
      .join('~');

    const subjectPart = (subjects || [])
      .map(s => {
        const rooms = (s.allowedRooms || []).slice().sort().join(',');
        return `${s.id || s.name}|${rooms}`;
      })
      .sort()
      .join('~');

    return this.hashString(`${teacherPart}#${groupPart}#${subjectPart}`);
  }

  /**
   * Full context fingerprint: every peer entry that can affect conflicts + assets.
   * When any peer moves/resizes/changes teacher/groups, this value changes and
   * all entry cache keys miss — fixing stale "green while neighbor is red".
   */
  buildContextFingerprint(
    entries: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[]
  ): string {
    const schedulePart = (entries || [])
      .filter(e => e && e.type !== 'event' && typeof e.isScheduled === 'function' && e.isScheduled())
      .map(e => this.fingerprintEntry(e))
      .sort()
      .join('||');

    const assetsPart = this.fingerprintAssets(teachers, groups, subjects);
    return this.hashString(`${schedulePart}##${assetsPart}`);
  }

  /**
   * Cache key = entry identity + entry self-fingerprint + schedule/asset context.
   */
  generateCacheKey(entry: ScheduleEntry, contextFingerprint: string): string {
    return `${entry.id}|${this.fingerprintEntry(entry)}|${contextFingerprint}`;
  }

  /**
   * FNV-1a 32-bit hash — short, stable, good enough for cache keys.
   */
  private hashString(input: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
  }

  private isCacheValid(timestamp: Date): boolean {
    return Date.now() - timestamp.getTime() < this.CACHE_TTL;
  }

  /**
   * Clear cache for a specific entry (any key starting with that id)
   */
  private clearCacheForEntry(entryId: string): void {
    const prefix = `${entryId}|`;
    const keysToDelete: string[] = [];
    Array.from(this.conflictCache.keys()).forEach(key => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.conflictCache.delete(key));
  }

  clearCache(): void {
    this.conflictCache.clear();
    console.log('[ConflictManager] Cache cleared');
  }

  getCacheStats(): { size: number; validEntries: number } {
    const now = Date.now();
    let validEntries = 0;

    Array.from(this.conflictCache.values()).forEach(cached => {
      if (now - cached.timestamp.getTime() < this.CACHE_TTL) {
        validEntries++;
      }
    });

    return {
      size: this.conflictCache.size,
      validEntries
    };
  }

  makeConflictId(itemId: string, reason: string): string {
    return makeDismissedConflictId(itemId, reason);
  }

  isConflictDismissed(id: string): boolean {
    return isConflictDismissed(id);
  }

  toggleConflictDismissed(id: string): boolean {
    return toggleConflictDismissed(id);
  }

  cleanupDismissedConflicts(): void {
    cleanupDismissed();
  }

  filterActiveConflictReasons<T extends { id: string }>(reasons: T[]): T[] {
    return filterActiveDismissedReasons(reasons);
  }

  onDismissedChange(listener: () => void): () => void {
    return onDismissedChange(listener);
  }
}

export const conflictManager = ConflictManager.getInstance();
export default ConflictManager;
